import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Clock, AlertTriangle, Circle, ChevronRight, Loader } from "lucide-react";
import { groundOpsApi } from "@/api";

const STATUS_COLOR = {
  not_started: "bg-slate-700/50 text-slate-400 border-slate-600/30",
  in_progress: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  delayed:     "bg-red-500/10 text-red-400 border-red-500/30",
  blocked:     "bg-purple-500/10 text-purple-400 border-purple-500/30",
};
const STATUS_ICONS = {
  not_started: Circle,
  in_progress: Clock,
  delayed:     AlertTriangle,
  blocked:     AlertTriangle,
  completed:   CheckCircle,
};

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" });
}

function urgency(task) {
  const diff = (new Date(task.planned_time) - Date.now()) / 60000;
  if (diff < 0 && task.status !== "completed") return "overdue";
  if (diff < 10) return "soon";
  return "normal";
}

export default function RampMyTasks({ user }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    groundOpsApi.getMyTasks(user.role, user.station)
      .then(setTasks)
      .finally(() => setLoading(false));
    const t = setInterval(() => {
      groundOpsApi.getMyTasks(user.role, user.station).then(setTasks);
    }, 30000);
    return () => clearInterval(t);
  }, [user.role, user.station]);

  if (loading) return <div className="p-6 text-center text-slate-500 animate-pulse">Loading your tasks...</div>;

  const overdue = tasks.filter(t => urgency(t) === "overdue");
  const soon    = tasks.filter(t => urgency(t) === "soon");
  const normal  = tasks.filter(t => urgency(t) === "normal");

  function TaskCard({ task }) {
    const urg = urgency(task);
    const Icon = STATUS_ICONS[task.status] || Circle;
    return (
      <div
        onClick={() => navigate(`/ramp/task/${task.id}`)}
        className={`flex items-center gap-3 p-4 rounded-2xl border mb-2 cursor-pointer active:scale-98 transition-transform ${STATUS_COLOR[task.status] || "bg-[#0c1929] border-slate-600/30"} ${urg === "overdue" ? "border-red-500/50" : ""}`}
        data-testid={`my-task-${task.code}`}
      >
        <Icon size={20} className={urg === "overdue" ? "text-red-400" : urg === "soon" ? "text-amber-400" : "text-slate-400"} />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-white truncate">{task.name}</div>
          <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-orange-400">{task.flight_number}</span>
            <span>{task.route}</span>
            {task.stand && <span>· Stand {task.stand}</span>}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`text-sm font-mono font-bold ${urg === "overdue" ? "text-red-400" : urg === "soon" ? "text-amber-400" : "text-slate-300"}`}>
            {fmtTime(task.planned_time)}
          </div>
          {urg === "overdue" && <div className="text-[9px] text-red-400 font-bold uppercase">OVERDUE</div>}
          {urg === "soon"    && <div className="text-[9px] text-amber-400 uppercase">Due Soon</div>}
        </div>
        <ChevronRight size={16} className="text-slate-600" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <h2 className="text-base font-black text-white">My Tasks</h2>
        <p className="text-[11px] text-slate-500">{tasks.length} assigned · {user.station}</p>
      </div>

      {tasks.length === 0 && (
        <div className="text-center py-12 text-slate-600">
          <CheckCircle size={40} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm">No tasks assigned at {user.station}</p>
        </div>
      )}

      {overdue.length > 0 && (
        <div className="mb-4">
          <div className="text-[9px] text-red-400 uppercase tracking-widest font-bold mb-2 flex items-center gap-1">
            <AlertTriangle size={9} /> Overdue ({overdue.length})
          </div>
          {overdue.map(t => <TaskCard key={t.id} task={t} />)}
        </div>
      )}

      {soon.length > 0 && (
        <div className="mb-4">
          <div className="text-[9px] text-amber-400 uppercase tracking-widest font-bold mb-2">Due Soon ({soon.length})</div>
          {soon.map(t => <TaskCard key={t.id} task={t} />)}
        </div>
      )}

      {normal.length > 0 && (
        <div className="mb-4">
          <div className="text-[9px] text-slate-500 uppercase tracking-widest font-bold mb-2">Upcoming ({normal.length})</div>
          {normal.map(t => <TaskCard key={t.id} task={t} />)}
        </div>
      )}
    </div>
  );
}
